import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Loader2, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Brand";
import { toast } from "sonner";

const HOST_TYPES = ["Host privato", "Property Manager", "Hotel o B&B"];
const COUNTS = ["1", "2-5", "6-20", "20+"];
const COUNTRIES = ["Italia", "Stati Uniti", "Regno Unito", "Spagna", "Francia", "Germania", "Portogallo", "Altro"];

export default function Onboarding() {
  const { user, ready, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", country: "Italy", host_type: HOST_TYPES[0], property_count_estimate: "1", preferred_language: "en" });
  const [loading, setLoading] = useState(false);

  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarded) return <Navigate to="/dashboard" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/onboarding", form);
      setUser(data.user);
      toast.success("Benvenuto in StayGuide!");
      navigate("/dashboard/properties/new");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans grain-bg flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg bg-white rounded-3xl border border-slate-100 shadow-xl p-8 sm:p-10">
        <div className="mb-6"><Logo className="h-10" /></div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight text-slate-900">Iniziamo</h1>
        <p className="mt-2 text-slate-500">Poche domande e creiamo la tua prima struttura.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="onboarding-form">
          <Field label="Nome">
            <input data-testid="ob-firstname" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="es. Marco" className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" required />
          </Field>
          <Field label="Paese">
            <select data-testid="ob-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500 bg-white">
              {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Sono un…">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {HOST_TYPES.map((h) => (
                <button key={h} type="button" data-testid={`ob-hosttype-${h}`} onClick={() => set("host_type", h)}
                  className={`text-sm rounded-xl border px-3 py-2.5 font-medium transition-colors ${form.host_type === h ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  {h}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Quante strutture?">
            <div className="flex gap-2">
              {COUNTS.map((c) => (
                <button key={c} type="button" onClick={() => set("property_count_estimate", c)}
                  className={`flex-1 text-sm rounded-xl border px-3 py-2.5 font-medium transition-colors ${form.property_count_estimate === c ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  {c}
                </button>
              ))}
            </div>
          </Field>
          <button data-testid="ob-submit" type="submit" disabled={loading} className="w-full rounded-xl bg-blue-500 text-white font-semibold py-3.5 hover:bg-blue-600 disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Crea la mia prima struttura <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
    {children}
  </div>
);
